import { useCallback, useState } from 'react';
import { type DropzoneOptions } from 'react-dropzone';
import { useFieldArray, useForm } from 'react-hook-form';

import {
  FileDropzone,
  FormBase,
  FormModal,
  type IFormModalProps,
  formatSize,
} from '@gbs-monorepo-packages/common';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  type UploadFileSchema,
  uploadMultipleFileSchema,
} from '../../../../../../formSchemas/documentSchema';
import {
  ActionButton,
  Checkbox,
  DeleteIcon,
  ErrorMessage,
  FileContainer,
  FileDetails,
  FileDropzoneContainer,
  FileIconCustom,
  FileName,
  FileSize,
  FilesContainer,
  HighlightText,
  InputText,
} from './styles';

export interface IUploadFileModalProps extends Partial<
  Omit<IFormModalProps, 'onAccept' | 'onOpenChange'>
> {
  dropzoneOptions?: DropzoneOptions;
  onAccept: (
    uploaded: File[],
    description: string[],
    highlighted: boolean[]
  ) => Promise<boolean>;
  onClearError?: () => void;
  onDecline?: () => void;
  onOpenChange?: () => void;
  open: boolean;
  waitToOpen?: boolean;
}

const HIGHLIGHT_LIMIT = 10;

export const UploadMultipleFileModal = ({
  children,
  dropzoneOptions,
  errorMessage,
  onAccept,
  onClearError,
  onDecline,
  open,
  waitToOpen = false,
  zIndex,
  onOpenChange,
  ...props
}: IUploadFileModalProps): JSX.Element | null => {
  const [loadingUploadFile, setLoadingUploadFile] = useState<boolean>(false);

  const documents = useForm<UploadFileSchema>({
    resolver: zodResolver(uploadMultipleFileSchema),
    mode: 'all',
    defaultValues: {
      files: [],
    },
  });

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
    clearErrors,
    trigger,
  } = documents;

  const files = watch('files');

  const resetForm = useCallback(() => {
    reset();
    setValue('files', []);
    setLoadingUploadFile(false);
  }, [reset, setValue]);

  const handleUploadFile = (data: UploadFileSchema) => {
    setLoadingUploadFile(true);
    const { files } = data;
    onAccept(
      files.map((item) => item.file),
      files.map((item) => item.description ?? ''),
      files.map((item) => item.highlighted)
    )
      .then((result: boolean) => {
        setLoadingUploadFile(false);
        if (result) {
          resetForm();
        }
      })
      .catch(() => {
        setLoadingUploadFile(false);
      });
  };

  const handleDeclineUploadFile = useCallback(() => {
    onDecline?.();
    onOpenChange?.();
  }, [onDecline, onOpenChange]);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      clearErrors('files');
      const newFiles = acceptedFiles.map((file) => ({
        file,
        description: '',
        highlighted: false,
      }));

      const updatedFiles = [...files, ...newFiles];
      setValue('files', updatedFiles);

      await trigger('files');
    },
    [clearErrors, files, setValue, trigger]
  );

  const toggleHighlight = useCallback(
    (index: number) => {
      const updatedFiles = [...files];
      updatedFiles[index].highlighted = !updatedFiles[index].highlighted;
      setValue('files', updatedFiles);
      const highlightedCount = updatedFiles.filter((f) => f.highlighted).length;
      if (highlightedCount <= HIGHLIGHT_LIMIT) {
        onClearError?.();
      }
    },
    [files, onClearError, setValue]
  );

  const { fields, remove } = useFieldArray({
    name: 'files',
    control,
  });

  const handleRemoveFile = useCallback(
    async (index: number) => {
      remove(index);
      const remainingFiles = files.filter((_, i) => i !== index);
      const highlightedCount = remainingFiles.filter(
        (f) => f.highlighted
      ).length;
      if (highlightedCount <= HIGHLIGHT_LIMIT) {
        onClearError?.();
      }
      await trigger('files');
    },
    [files, onClearError, remove, trigger]
  );

  return !open ? null : (
    <FormBase.Provider {...documents}>
      <FormModal
        loading={loadingUploadFile}
        dataCy="upload-file-modal"
        acceptText="Upload"
        mainText="Upload file"
        {...props}
        errorMessage={errorMessage}
        open={!waitToOpen}
        onAccept={handleSubmit(handleUploadFile)}
        onDecline={handleDeclineUploadFile}
        onOpenChange={handleDeclineUploadFile}
        zIndex={zIndex}
      >
        <FormBase.Content>
          {children}

          {errors.files && errors.files.type !== 'too_big' && (
            <ErrorMessage>{errors.files.message}</ErrorMessage>
          )}
          <FileDropzoneContainer>
            <FileDropzone
              onDrop={handleDrop}
              {...dropzoneOptions}
              disabled={props.disabled ?? loadingUploadFile}
            />
          </FileDropzoneContainer>

          {!!files.length && (
            <>
              <HighlightText>
                {
                  'Use the checkboxes to highlight resources on the Client Portal Dashboard.'
                }
              </HighlightText>
              <FilesContainer>
                {fields.map((file, index) => (
                  <FileContainer key={index}>
                    <FormBase.CheckboxContent
                      label=""
                      htmlFor={`highlightDocumentCheckbox-${index}`}
                    >
                      <Checkbox
                        id={`files.${index}.highlighted`}
                        checked={file.highlighted}
                        name={`files.${index}.highlighted`}
                        dataCy={`highlight-document-checkbox-${index}`}
                        onCheckedChange={() => {
                          toggleHighlight(index);
                        }}
                        disabled={loadingUploadFile}
                      ></Checkbox>
                    </FormBase.CheckboxContent>
                    <FileIconCustom file={file.file} dataCy={'logo-image'} />
                    <FileDetails>
                      <FileName>{file.file.name}</FileName>
                      <FileSize>{formatSize(file.file.size)}</FileSize>
                      <InputText
                        type="text"
                        id={`files.${index}.description`}
                        name={`files.${index}.description`}
                        placeholder="Description (Optional)"
                        disabled={loadingUploadFile}
                        data-cy={`input-description-${index}`}
                        maxLength={50}
                      />
                      {errors.files?.[index]?.description && (
                        <ErrorMessage>
                          {errors.files?.[index]?.description?.message}
                        </ErrorMessage>
                      )}
                    </FileDetails>
                    <ActionButton
                      disabled={loadingUploadFile}
                      type="button"
                      onClick={() => {
                        handleRemoveFile(index);
                      }}
                    >
                      <DeleteIcon />
                    </ActionButton>
                  </FileContainer>
                ))}
              </FilesContainer>
              {errors.files && errors.files.type === 'too_big' && (
                <ErrorMessage>{errors.files.message}</ErrorMessage>
              )}
              {Boolean(errors.files?.root) && (
                <ErrorMessage>{errors.files?.root?.message}</ErrorMessage>
              )}
            </>
          )}
        </FormBase.Content>
      </FormModal>
    </FormBase.Provider>
  );
};
